import { RefObject } from "react";
import moment from "moment-timezone";
import { PositionedEvent } from "../types";

export interface CalendarHandlersParams {
  containerRef: RefObject<HTMLDivElement>;
  numDays: number;
  slotHeight: number;
  minDurationMinutes: number;
  startDate: Date;
  timezone: string;
  dragged: PositionedEvent | null;
  resizing: PositionedEvent | null;
  creating: { startY: number; dayIndex: number; startTime: Date } | null;
  setDragged: (event: PositionedEvent | null) => void;
  setResizing: (event: PositionedEvent | null) => void;
  setCreating: (creating: { startY: number; dayIndex: number; startTime: Date } | null) => void;
  onUpdate: (eventId: string, newStart: Date, newEnd: Date) => void;
  onCreate: (start: Date, end: Date) => void;
}

export function createMouseHandlers({
  containerRef,
  numDays,
  slotHeight,
  minDurationMinutes,
  startDate,
  timezone,
  dragged,
  resizing,
  creating,
  setDragged,
  setResizing,
  setCreating,
  onUpdate,
  onCreate,
}: CalendarHandlersParams) {
  const handleMouseDown = (e: React.MouseEvent, event: PositionedEvent) => {
    e.preventDefault();
    setDragged(event);
  };

  const handleResizeStart = (e: React.MouseEvent, event: PositionedEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(event);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (creating && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const offsetY = e.clientY - rect.top;
      const start = new Date(creating.startTime);
      const pixelDifference = Math.max(offsetY - creating.startY, 0); // Ensure positive
      const hours = pixelDifference / slotHeight;
      const minutes = hours * 60;
      const chunks = minutes / minDurationMinutes;
      const roundedChunks = Math.max(Math.ceil(chunks), 1); // At least 1 chunk
      const durationMin = roundedChunks * minDurationMinutes;
      const end = new Date(start.getTime() + durationMin * 60000);
      setCreating({ ...creating, startTime: start, startY: creating.startY });
      setResizing({
        id: "new",
        title: "New Event",
        start,
        end,
        top: creating.startY,
        height: durationMin * (slotHeight / 60),
        left: 0,
        width: 1,
        dayIndex: creating.dayIndex,
        zIndex: 1000,
      });
      return;
    }

    if ((!dragged && !resizing) || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const dayWidth = rect.width / numDays;
    const dayIndex = Math.floor(offsetX / dayWidth);
    const minutes = Math.floor((offsetY / slotHeight) * 60 / 15) * 15;

    if (dragged) {
      // Create new start time in the calendar's timezone context
      const newStartMoment = moment.tz(startDate, timezone).add(dayIndex, "days");
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      newStartMoment.hours(hours).minutes(mins).seconds(0).milliseconds(0);

      // Preserve the original duration
      const originalDuration = moment(dragged.end).diff(moment(dragged.start), "milliseconds");
      const start = newStartMoment.toDate();
      const end = new Date(start.getTime() + originalDuration);
      setDragged({ ...dragged, top: offsetY, start, end, dayIndex });
    } else if (resizing) {
      // Use timezone-aware calculations for resizing
      const startMoment = moment.tz(resizing.start, timezone);
      const startMinutes = startMoment.hours() * 60 + startMoment.minutes();
      const minutesFromStart = minutes - startMinutes;
      const snappedMinutes = Math.max(Math.ceil(minutesFromStart / minDurationMinutes) * minDurationMinutes, minDurationMinutes);
      const newEnd = new Date(resizing.start.getTime() + snappedMinutes * 60 * 1000);
      const newHeight = snappedMinutes * (slotHeight / 60);
      setResizing({ ...resizing, height: newHeight, end: newEnd });
    }
  };

  const handleMouseUp = () => {
    if (creating && resizing) {
      onCreate(resizing.start, resizing.end);
      setCreating(null);
      setResizing(null);
      return;
    }
    if (dragged) {
      onUpdate(dragged.id, dragged.start, dragged.end);
      setDragged(null);
    } else if (resizing) {
      onUpdate(resizing.id, resizing.start, resizing.end);
      setResizing(null);
    } else if (creating) {
      // Create event at the hour of the click with 1-hour default duration
      // Note: creating.startTime is already timezone-aware from CalendarGrid
      const startMoment = moment(creating.startTime);
      const hour = Math.floor(startMoment.minutes() / 60) + startMoment.hours();
      startMoment.hours(hour).minutes(0).seconds(0).milliseconds(0); // Set to the start of the hour

      const start = startMoment.toDate();
      const end = startMoment.clone().add(1, "hour").toDate(); // Add 1 hour
      onCreate(start, end);
      setCreating(null);
    }
  };

  return {
    handleMouseDown,
    handleResizeStart,
    handleMouseMove,
    handleMouseUp,
  };
}
